import { useLocation, useNavigate } from 'react-router';
import { useAuth0 } from '@auth0/auth0-react';
import BankNoteIcon from '@untitled-ui/icons-react/build/esm/BankNote03';
import DotsVerticalIcon from '@untitled-ui/icons-react/build/esm/DotsVertical';

import { Button, Dropdown } from 'antd';
import { BaseButton } from 'components';
import { twMerge } from 'tailwind-merge';
import { getInitialsFromCookies } from 'utils';
import { PRIME_DOCS_LOGO, PRIME_KYC_LOGO, PRIME_SEARCH_LOGO, USER_CHECK_ICON } from 'utils/icons';

import './Navigation.scss';

export const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, loginWithRedirect, isLoading } = useAuth0();

  const isActive = (path: string) => location.pathname.includes(path);

  const handleSignOut = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  const handleLogin = () => {
    loginWithRedirect({
      appState: {
        prompt: 'none',
      },
    });
  };

  const DROPDOWN_ITEMS = [
    {
      label: 'Sign Out',
      onClick: handleSignOut,
    },
  ];

  const items = DROPDOWN_ITEMS.map((item, index) => ({
    key: index.toString(),
    label: <span>{item.label}</span>,
    onClick: item.onClick,
  }));

  return (
    <aside
      id="default-sidebar"
      className="z-40 md:w-[264px] h-screen transition-transform -translate-x-full sm:translate-x-0 hidden sm:block"
      aria-label="Sidebar"
    >
      <div className="h-full px-2 py-4 overflow-y-auto bg-navy-900 min-w-[224px]">
        <div className="flex flex-col items-center h-full justify-between">
          <div className="flex flex-col w-full">
            <div className="flex items-center px-3 py-1 justify-between">
              <img src={PRIME_KYC_LOGO} className="h-5" alt="Prime Logo" />
            </div>

            <div className="flex items-center justify-center w-full h-[40px] mb-[16px]" />

            <ul className="space-y-2 font-medium">
              <li onClick={() => navigate('know-your-customer')}>
                <a
                  className={twMerge(
                    'nav-button',
                    `${isActive('know-your-customer') ? 'bg-navy-700' : ''}`
                  )}
                >
                  <img src={USER_CHECK_ICON} alt="Know Your Customer" />
                  <span className="ms-3">Know Your Customer</span>
                </a>
              </li>
              {/* TODO WIP */}
              <li onClick={() => navigate('billing')}>
                <a className={twMerge('nav-button', `${isActive('billing') ? 'bg-navy-700' : ''}`)}>
                  <BankNoteIcon className="text-gray-400" />
                  <span className="ms-3">Billing</span>
                </a>
              </li>
            </ul>
          </div>

          <div className="flex flex-col w-full gap-2">
            <a
              href={process.env.REACT_APP_PRIMESEARCH_URL + '/api/auth/login?prompt=none'}
              rel="noopener noreferrer"
              className="block"
            >
              <div
                style={{ borderColor: 'rgba(241,245,249,.1)' }}
                className="px-6 py-5 bg-[#03182E] rounded-md border-2 border-slate-100/10 drop-shadow-md
                hover:cursor-pointer hover:border-[#14C098]/50"
              >
                <img src={PRIME_SEARCH_LOGO} className="h-[13.5px]" alt="Prime Search Logo" />
              </div>
            </a>
            <a
              href={process.env.REACT_APP_PRIMEDOCS_URL + '/auth/login?prompt=none'}
              rel="noopener noreferrer"
              className="block"
            >
              <div
                style={{ borderColor: 'rgba(241,245,249,.1)' }}
                className="px-6 py-5 bg-[#03182E] rounded-md border-2 border-slate-100/10 drop-shadow-md
                hover:cursor-pointer hover:border-[#14C098]/50"
              >
                <img src={PRIME_DOCS_LOGO} className="h-[12.5px]" alt="Prime Documents Logo" />
              </div>
            </a>
            {user ? (
              <div className="mt-4 flex items-center justify-between pt-4 border-solid border-t-[1px] border-[#364152] ">
                <div className="flex items-center px-3 py-2 text-[#EEF2F6] rounded-lg group font-medium text-[14px]">
                  {getInitialsFromCookies(user.name ?? 'User')}
                  <span className="ms-3">{user.email}</span>
                </div>
                <Dropdown menu={{ items }} trigger={['click']}>
                  <Button type="link" className="p-0">
                    <DotsVerticalIcon className="text-gray-400 h-5 w-5" />
                  </Button>
                </Dropdown>
              </div>
            ) : (
              <BaseButton
                className="secondary-button min-w-fit mt-2"
                onClick={handleLogin}
                loading={isLoading}
              >
                Login / Sign up
              </BaseButton>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
};
